import { apiService } from "./api.service";
import { formatService } from "./format.service";
import { injectable } from "inversify";
import IEnquiryService from "@/services/interfaces/enquiry.service.interface";
import { IResponseModel } from "@/models/interfaces/response.model.interface";
import { IPropertyEnquirySummary } from "@/models/interfaces/enquiry/property-enquiry-summary.model.interface";
import { IZoneEnquirySummary } from "@/models/interfaces/enquiry/zone-enquiry-summary-model.interface";
import { IDetailedEnquirySummary } from "@/models/interfaces/enquiry/detailed-enquiry-summary.model.interface";
import { ICreativeEnquirySummary } from "@/models/interfaces/enquiry/creative-enquiry-summary.model.interface";
import { DetailedEnquirySummary } from "@/models/enquiry/detailed-enquiry-summary.model";
import { IContentSetVersionEnquirySummary } from "@/models/interfaces/enquiry/content-set-version-enquiry-summary.model.interface";
import { ContentSetVersionEnquirySummary } from "@/models/enquiry/content-set-version-enquiry-summary.model";
import { EnquirySource } from '@/models/enquiry/enquiry-source.model';

@injectable()
export default class EnquiryService implements IEnquiryService {
    public async getEnquiries(startDate: Date, endDate: Date): Promise<IResponseModel<IPropertyEnquirySummary[]>> {
        return apiService.get(`/api/v1/performance/enquiry/summary?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
            .then((response) => {
                const result = response.data as IResponseModel<IPropertyEnquirySummary[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IPropertyEnquirySummary[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async getPropertyEnquiries(propertyId: number, startDate: Date, endDate: Date): Promise<IResponseModel<IDetailedEnquirySummary>> {
        return apiService.get(`/api/v1/performance/enquiry/summary/property/${propertyId}?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
            .then((response) => {
                const result = response.data as IResponseModel<IDetailedEnquirySummary>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IDetailedEnquirySummary> = {
                    success: false,
                    result: new DetailedEnquirySummary()
                };
                return result;
            });
    }

    public async getEnquirySummaryBySource(enquirySource: EnquirySource, startDate: Date, endDate: Date): Promise<IResponseModel<IDetailedEnquirySummary[]>> {
        return apiService.get(`/api/v1/performance/enquiry/summary/property/${enquirySource.propertyId}/${enquirySource.source}?leadSource=${enquirySource.leadSource}&startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
            .then((response) => {
                const result = response.data as IResponseModel<IDetailedEnquirySummary[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IDetailedEnquirySummary[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async getZoneEnquiries(propertyId: number, startDate: Date, endDate: Date): Promise<IResponseModel<IZoneEnquirySummary[]>> {
        return apiService.get(`/api/v1/performance/enquiry/property/${propertyId}/zone?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
            .then((response) => {
                const result = response.data as IResponseModel<IZoneEnquirySummary[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IZoneEnquirySummary[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async getCreativeEnquiries(propertyId: number, startDate: Date, endDate: Date): Promise<IResponseModel<ICreativeEnquirySummary[]>> {
        return apiService.get(`/api/v1/performance/enquiry/property/${propertyId}/creative?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
            .then((response) => {
                const result = response.data as IResponseModel<ICreativeEnquirySummary[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<ICreativeEnquirySummary[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async getLiveContentSetVersionEnquiries(contentSetId: number): Promise<IResponseModel<IContentSetVersionEnquirySummary>> {
        return apiService.get(`/api/v1/performance/enquiry/contentset/${contentSetId}/version/live`)
            .then((response) => {
                const result = response.data as IResponseModel<IContentSetVersionEnquirySummary>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IContentSetVersionEnquirySummary> = {
                    success: false,
                    result: new ContentSetVersionEnquirySummary()
                };
                return result;
            });
    }

    public async getPreviousContentSetVersionEnquiries(contentSetId: number): Promise<IResponseModel<IContentSetVersionEnquirySummary[]>> {
        return apiService.get(`/api/v1/performance/enquiry/contentset/${contentSetId}/version/previous`)
            .then((response) => {
                const result = response.data as IResponseModel<IContentSetVersionEnquirySummary[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IContentSetVersionEnquirySummary[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }
}
